// File upload con react-dropzone — supporta immagini e PDF
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, X, File, Image } from 'lucide-react'
import { cn } from '../../lib/utils'

interface UploadedFile {
  file: File
  preview?: string
}

interface FileUploadProps {
  files: UploadedFile[]
  onAdd: (files: UploadedFile[]) => void
  onRemove: (index: number) => void
  accept?: Record<string, string[]>
  maxFiles?: number
  maxSize?: number
  disabled?: boolean
  className?: string
  label?: string
}

export function FileUpload({
  files,
  onAdd,
  onRemove,
  accept = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    'application/pdf': ['.pdf'],
  },
  maxFiles = 10,
  maxSize = 10 * 1024 * 1024, // 10MB
  disabled,
  className,
  label = 'Trascina file qui o clicca per selezionare',
}: FileUploadProps) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      const newFiles: UploadedFile[] = accepted.map((f) => ({
        file: f,
        preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      }))
      onAdd(newFiles)
    },
    [onAdd]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: maxFiles - files.length,
    maxSize,
    disabled,
  })

  return (
    <div className={cn('space-y-3', className)}>
      <div
        {...getRootProps()}
        className={cn(
          'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors',
          'border-[var(--fc-border)] hover:border-[var(--fc-primary)] hover:bg-[color-mix(in_srgb,var(--fc-primary)_4%,transparent)]',
          isDragActive && 'border-[var(--fc-primary)] bg-[color-mix(in_srgb,var(--fc-primary)_8%,transparent)]',
          disabled && 'cursor-not-allowed opacity-50 hover:border-[var(--fc-border)] hover:bg-transparent'
        )}
        role="button"
        aria-label="Area caricamento file"
      >
        <input {...getInputProps()} aria-label="Carica file" />
        <Upload className="mb-2 h-8 w-8 text-[var(--fc-foreground-subtle)]" aria-hidden="true" />
        <p className="text-sm font-medium text-[var(--fc-foreground)]">{label}</p>
        <p className="mt-1 text-xs text-[var(--fc-foreground-muted)]">
          PDF, JPG, PNG — max {Math.round(maxSize / 1024 / 1024)}MB
        </p>
      </div>

      {/* Anteprima file */}
      {files.length > 0 && (
        <ul className="space-y-2" role="list" aria-label="File caricati">
          {files.map((uploaded, index) => (
            <li
              key={index}
              className="flex items-center gap-3 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] px-3 py-2"
            >
              {uploaded.preview ? (
                <img src={uploaded.preview} alt="" className="h-10 w-10 rounded object-cover" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded bg-[var(--fc-surface-overlay)]">
                  {uploaded.file.type.startsWith('image/') ? (
                    <Image className="h-5 w-5 text-[var(--fc-foreground-muted)]" />
                  ) : (
                    <File className="h-5 w-5 text-[var(--fc-foreground-muted)]" />
                  )}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--fc-foreground)]">
                  {uploaded.file.name}
                </p>
                <p className="text-xs text-[var(--fc-foreground-muted)]">
                  {(uploaded.file.size / 1024).toFixed(0)} KB
                </p>
              </div>
              <button
                onClick={() => onRemove(index)}
                className="shrink-0 text-[var(--fc-foreground-muted)] hover:text-[var(--fc-danger)] transition-colors"
                aria-label={`Rimuovi ${uploaded.file.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
